import chroma from 'chroma-js';
import { useFormContext, useWatch } from 'react-hook-form';
import { Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import type { EventScorekeepingScoreFormValues } from '@/components/Event/EventScorekeeping/EventScorekeepingScore/EventScorekeepingScore.types';
import Box from '@/components/UI/Box';
import TextInput from '@/components/UI/TextInput';
import Icon from '@/components/Unistyles/Icon';
import getOffsetColor from '@/utils/getOffsetColor';
import hapticsOnPress from '@/utils/hapticsOnPress';

type EventScorekeepingScoreInputProps = {
  id: string;
  color: string;
  teamTitle: string;
};
export default function EventScorekeepingScoreInput({
  id,
  color,
  teamTitle,
}: EventScorekeepingScoreInputProps) {
  const { getValues, setValue } =
    useFormContext<EventScorekeepingScoreFormValues>();
  const { scores } = useWatch<EventScorekeepingScoreFormValues>();
  const inputValue = scores?.[id];
  const onPressIncrement = () => {
    const formScores = getValues('scores');
    const intValue =
      Math.round(Number.parseFloat(formScores?.[id] || '0') * 100) / 100;
    if (intValue >= 0) {
      const incrementedValue = Math.round((intValue + 1) * 100) / 100;
      setValue(
        'scores',
        {
          ...formScores,
          [id]: incrementedValue.toString(),
        },
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    }
  };
  const onPressDecrement = () => {
    const formScores = getValues('scores');
    const intValue = Number.parseFloat(formScores?.[id] || '0');
    if (intValue > 0) {
      const decrementedValue =
        Math.round((intValue - Math.min(intValue, 1)) * 100) / 100;
      setValue(
        'scores',
        {
          ...formScores,
          [id]: decrementedValue.toString(),
        },
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    }
  };
  const onChangeText = (text: string) => {
    const formScores = getValues('scores');
    const regex = /^(\d{1,3}|\d{1,3}\.\d{0,2}|\.\d{0,2})$/;
    const isValid = regex.test(text);
    const shouldReplaceZeroWithDigit =
      text.length === 2 && /^0[1-9]$/.test(text);
    if (isValid && shouldReplaceZeroWithDigit) {
      setValue(
        'scores',
        {
          ...formScores,
          [id]: text.charAt(1),
        },
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    } else if (isValid || !text) {
      setValue(
        'scores',
        {
          ...formScores,
          [id]: text,
        },
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    }
  };
  const buttonColor = chroma(color).alpha(0.95).hex();
  const activeButtonColor = chroma(color).darken().hex();
  const buttonTextColor = getOffsetColor(color, false);
  return (
    <Box direction="horizontal" h="full">
      <Pressable
        accessibilityLabel={`Decrease ${teamTitle} score`}
        onPress={hapticsOnPress(onPressDecrement)}
        style={({ pressed }) =>
          style.scoreButton({
            activeBackgroundColor: activeButtonColor,
            backgroundColor: buttonColor,
            pressed,
          })
        }
        testID={`EventScorekeepingScoreDecrement-${id}`}
      >
        <Icon name="remove-outline" color={buttonTextColor} />
      </Pressable>
      <Box h="full" overflow="hidden">
        <TextInput
          accessibilityLabel={`${teamTitle} score`}
          variant="plain"
          containerStyle={style.scoreInputContainer(color)}
          style={style.scoreInput(buttonTextColor)}
          textAlign="center"
          maxLength={6}
          onChangeText={onChangeText}
          value={scores?.[id]?.toString() || ''}
          enterKeyHint="done"
          inputMode="decimal"
          blurOnSubmit
          selectionColor={getOffsetColor(color, false)}
          selection={{
            start: inputValue?.length || 0,
            end: inputValue?.length || 0,
          }}
          testID={`EventScorekeepingScoreInput-${id}`}
        />
      </Box>
      <Pressable
        accessibilityLabel={`Increase ${teamTitle} score`}
        onPress={hapticsOnPress(onPressIncrement)}
        style={({ pressed }) =>
          style.scoreButton({
            activeBackgroundColor: activeButtonColor,
            backgroundColor: buttonColor,
            pressed,
          })
        }
        testID={`EventScorekeepingScoreIncrement-${id}`}
      >
        <Icon name="add-outline" color={buttonTextColor} />
      </Pressable>
    </Box>
  );
}
const style = StyleSheet.create((theme) => ({
  scoreInput: (color: string) => ({
    color,
    fontSize: theme.fontSize['3xl'],
    fontWeight: theme.fontWeight.bold,
    height: '100%',
    paddingVertical: 0,
    textAlignVertical: 'center',
  }),
  scoreInputContainer: (backgroundColor: string) => ({
    alignItems: 'center',
    backgroundColor,
    borderRadius: theme.borderRadius.none,
    borderWidth: 0,
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: 0,
    width: theme.size(24),
  }),
  scoreButton: ({
    activeBackgroundColor,
    backgroundColor,
    pressed,
  }: {
    activeBackgroundColor: string;
    backgroundColor: string;
    pressed: boolean;
  }) => ({
    alignItems: 'center',
    backgroundColor: pressed ? activeBackgroundColor : backgroundColor,
    borderRadius: theme.borderRadius.none,
    height: '100%',
    justifyContent: 'center',
    width: theme.size(8),
    _web: {
      _hover: {
        backgroundColor: activeBackgroundColor,
      },
    },
  }),
}));
